import { LOYALTY_POINTS_CODE, LOYALTY_PREFIX } from "@/lib/loyalty-benefits"
import type { OrderResponse } from "@/types/external"

export type OrderDiscountRow = { key: string; label: string; value: number }

type CodedRow = OrderDiscountRow & { code?: string }

type OrderDiscount = NonNullable<OrderResponse["discounts"]>[number]

/** Friendly PT-BR label for a discount, by its provenance code. */
export function discountLabel(code: string): string {
  if (code === LOYALTY_POINTS_CODE || code === "LOYALTY") return "Pontos de fidelidade"
  if (code.startsWith("LOYALTY_REWARD:")) return "Resgate com pontos"
  if (code.startsWith("LOYALTY_RULE_PROMO:")) return "Promoção da loja"
  if (code.startsWith(LOYALTY_PREFIX)) return "Benefício fidelidade"
  return code ? `Cupom ${code}` : "Desconto"
}

function targetLabel(target: string): string {
  return target === "DELIVERY_FEE" ? "Desconto na entrega" : "Desconto"
}

function rowsForDiscount(discount: OrderDiscount, index: number): CodedRow[] {
  const sponsorships = (discount.sponsorshipValues ?? []).filter((s) => (s.amount?.value ?? 0) > 0)
  if (sponsorships.length > 0) {
    return sponsorships.map((s, j) => ({
      key: `${discount.targetId || index}-${s.discountCode ?? j}`,
      label: discountLabel(s.discountCode ?? ""),
      value: s.amount.value,
      code: s.discountCode || undefined,
    }))
  }
  const value = discount.amount?.value ?? 0
  if (value <= 0) return []
  return [{ key: `${discount.targetId || index}-${discount.target}`, label: targetLabel(discount.target), value }]
}

/**
 * The discount lines of the order summary. Prefers the sponsorship split (one
 * row per coupon/loyalty code), then the per-discount amount, and finally the
 * aggregate `total.discount` when `discounts[]` is missing altogether, so the
 * customer never sees a subtotal + fees that don't add up to the total.
 */
/**
 * A coupon spread over several lines arrives as one discount per line; the
 * summary shows it once, with its shares added up.
 */
function mergeByCode(rows: CodedRow[]): OrderDiscountRow[] {
  const merged: OrderDiscountRow[] = []
  const byCode = new Map<string, OrderDiscountRow>()
  for (const row of rows) {
    const existing = row.code ? byCode.get(row.code) : undefined
    if (existing) {
      existing.value = Math.round((existing.value + row.value) * 100) / 100
      continue
    }
    const { code, ...rest } = row
    const entry: OrderDiscountRow = { ...rest }
    if (code) byCode.set(code, entry)
    merged.push(entry)
  }
  return merged
}

export function orderDiscountRows(order: OrderResponse): OrderDiscountRow[] {
  const rows = mergeByCode((order.discounts ?? []).flatMap(rowsForDiscount))
  if (rows.length > 0) return rows
  const aggregate = order.total?.discount?.value ?? 0
  if (aggregate <= 0) return []
  return [{ key: "total-discount", label: "Desconto", value: aggregate }]
}
